/* ASE Trade theme: core behaviour. No dependencies. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const formatMoney = (cents) => {
    const fmt = (window.ASE && ASE.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value);
  };

  /* Cart API */
  const Cart = {
    async add(items, opts = {}) {
      const res = await fetch(ASE.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: 'cart-drawer-body', sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || ASE.strings.error);
      Cart.render(data.sections);
      await Cart.refreshCount();
      if (!opts.skipGift) await Cart.syncGift();
      return data;
    },
    /* Free Pump Armor with a sprayer: the discount needs the bottle in the cart, so add it once when a sprayer is there. A shopper
       who removes it is not asked again this session. The drawer body carries data-has-sprayer, data-has-gift, data-gift-variant. */
    async syncGift() {
      const b = $('[data-cart-drawer-body]');
      if (!b || !b.dataset.giftVariant || !('hasSprayer' in b.dataset) || 'hasGift' in b.dataset) return;
      try { if (sessionStorage.getItem('ase_gift_off')) return; } catch (e) { /* storage blocked: still add once */ }
      await Cart.add([{ id: Number(b.dataset.giftVariant), quantity: 1 }], { skipGift: true });
    },
    async change(line, quantity) {
      const res = await fetch(ASE.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: 'cart-drawer-body', sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || ASE.strings.error);
      Cart.render(data.sections);
      Cart.setCount(data.item_count);
      return data;
    },
    async refreshCount() {
      const res = await fetch(ASE.routes.cart, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      Cart.setCount(cart.item_count);
      return cart;
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = n; el.dataset.count = n; });
    },
    render(sections) {
      if (!sections || !sections['cart-drawer-body']) return;
      const html = new DOMParser().parseFromString(sections['cart-drawer-body'], 'text/html');
      const next = html.querySelector('[data-cart-drawer-body]');
      const cur = $('[data-cart-drawer-body]');
      if (next && cur) { cur.replaceWith(next); Cart.enhance(next); }
    },
    /* Quantity stepper. The line markup comes from snippets/cart-drawer-content.liquid ("Qty N" plus Remove), so the stepper
       is built here for the cart page and the drawer alike, and re-built after every re-render. The free gift line keeps its
       plain "Qty" text so the offer cannot be stacked by hand. Lines are 1-based like data-cart-remove. */
    enhance(root) {
      Cart.styles();
      $$('[data-cart-remove]', root || document).forEach((rm) => {
        const row = rm.parentElement;
        if (!row || row.querySelector('.qty-step') || 'cartGift' in rm.dataset) return;
        const m = /Qty\s+(\d+)/.exec(row.textContent);
        if (!m) return;
        const title = (rm.getAttribute('aria-label') || '').replace(/^Remove\s+/, '') || 'item';
        const wrap = document.createElement('div');
        wrap.className = 'qty-step';
        wrap.dataset.qtyLine = rm.dataset.cartRemove;
        wrap.innerHTML = '<button type="button" class="qty-step__btn" data-qty-dec>\u2212</button>'
          + '<input class="qty-step__input" type="number" min="0" step="1" inputmode="numeric" pattern="[0-9]*" autocomplete="off">'
          + '<button type="button" class="qty-step__btn" data-qty-inc>+</button>';
        const input = wrap.querySelector('input');
        input.value = m[1];
        input.dataset.was = m[1];
        input.setAttribute('aria-label', 'Quantity of ' + title);
        wrap.querySelector('[data-qty-dec]').setAttribute('aria-label', 'One fewer ' + title);
        wrap.querySelector('[data-qty-inc]').setAttribute('aria-label', 'One more ' + title);
        Array.from(row.childNodes).forEach((n) => { if (n.nodeType === 3) n.remove(); });
        row.classList.add('qty-row');
        row.insertBefore(wrap, rm);
      });
      Cart.limits(root);
    },
    /* Per line maximum from cart.js (quantity_rule.max), applied to the inputs. A blank max means no limit is published. */
    async limits(root) {
      const inputs = $$('.qty-step__input', root || document);
      if (!inputs.length) return;
      try {
        const cart = await Cart.refreshCount();
        inputs.forEach((input) => {
          const line = Number(input.closest('.qty-step').dataset.qtyLine);
          const rule = cart.items[line - 1] && cart.items[line - 1].quantity_rule;
          if (rule && rule.max) input.max = rule.max;
        });
      } catch (e) { /* no limit shown; the server still refuses an over-limit change */ }
    },
    styles() {
      if (document.getElementById('ase-qty-style')) return;
      const st = document.createElement('style');
      st.id = 'ase-qty-style';
      st.textContent = '.qty-step{display:inline-flex;align-items:stretch;border:1px solid var(--c-line);border-radius:var(--r-sm);background:var(--c-paper)}'
        + '.qty-step__btn{min-width:44px;min-height:44px;border:0;background:none;color:inherit;font:inherit;font-size:20px;line-height:1;cursor:pointer;padding:0}'
        + '.qty-step__btn:hover{background:var(--c-paper-2)}'
        + '.qty-step__input{width:48px;min-height:44px;border:0;border-left:1px solid var(--c-line);border-right:1px solid var(--c-line);border-radius:0;text-align:center;font:inherit;font-size:16px;background:none;color:inherit;padding:0;-moz-appearance:textfield;appearance:textfield}'
        + '.qty-step__input::-webkit-outer-spin-button,.qty-step__input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}'
        + '.qty-step[aria-busy="true"]{opacity:.6;pointer-events:none}'
        + '.cart-line>div{min-width:0}.qty-row{flex-wrap:wrap}'
        + '.cart-line__remove{min-height:44px;margin:0 0 0 4px;padding:0 10px}';
      document.head.appendChild(st);
    },
    announce(msg) {
      let live = document.getElementById('CartLive');
      if (!live) {
        live = document.createElement('div');
        live.id = 'CartLive';
        live.className = 'visually-hidden';
        live.setAttribute('role', 'status');
        live.setAttribute('aria-live', 'polite');
        document.body.appendChild(live);
      }
      live.textContent = '';
      setTimeout(() => { live.textContent = msg; }, 50);
    },
    /* Set one line's quantity from the stepper; refuses nothing itself, the server's answer decides. */
    async setLine(wrap, qty) {
      const input = wrap.querySelector('input');
      const was = input.dataset.was;
      const max = Number(input.max) || 0;
      if (!Number.isFinite(qty) || qty < 0) qty = Number(was);
      if (max && qty > max) qty = max;
      if (String(qty) === was) { input.value = was; return; }
      const label = input.getAttribute('aria-label').replace(/^Quantity of /, '');
      wrap.setAttribute('aria-busy', 'true');
      try {
        const data = await Cart.change(Number(wrap.dataset.qtyLine), qty);
        Cart.announce((qty === 0 ? label + ' removed. ' : 'Quantity of ' + label + ' is now ' + qty + '. ') + 'Subtotal ' + formatMoney(data.total_price) + '.');
      } catch (err) {
        wrap.removeAttribute('aria-busy');
        input.value = was;
        Cart.announce('Could not change the quantity of ' + label + '. ' + err.message);
      }
    },
    /* Re-read the drawer body from the server. A page restored by Back (bfcache or the HTTP cache) carries the
       drawer as it was when the page first loaded, so a cart changed on another page would show stale or empty. */
    async refresh() {
      /* The home page, not this one: a product URL asked for JSON answers with the product, not the section */
      const res = await fetch('/?sections=cart-drawer-body');
      if (res.ok) Cart.render(await res.json());
      return Cart.refreshCount();
    }
  };
  window.ASE = Object.assign(window.ASE || {}, { Cart, formatMoney });
  Cart.enhance(document);

  /* Drawers */
  /* Focus handling: the page behind an open drawer is made inert (main, header, footer; never one that holds the drawer, since
     the menu drawer sits inside the header's section), Tab wraps inside the panel, and closing hands focus back to whatever
     opened it. The opener is passed in by the click and add-to-cart paths; a gone or disabled opener falls back to the header
     cart or menu button. */
  const FOCUSABLE = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
  const drawerFocusables = (d) => $$(FOCUSABLE, d.querySelector('.drawer__panel') || d).filter((x) => !x.disabled && !x.closest('[inert]') && !x.closest('[hidden]') && x.getClientRects().length > 0 && (!x.checkVisibility || x.checkVisibility()));
  const openDrawer = (id, opener) => {
    const d = document.getElementById(id);
    if (!d) return;
    if (id === 'CartDrawer' && d._stale) { d._stale = false; Cart.refresh().catch(() => {}); }
    if (!d.classList.contains('is-open')) {
      d._opener = opener || document.activeElement;
      d._inerted = $$('#MainContent, header, footer').filter((el) => !el.contains(d) && !el.hasAttribute('inert'));
      d._inerted.forEach((el) => el.setAttribute('inert', ''));
    }
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const focusable = d.querySelector('button, a, input');
    if (focusable) setTimeout(() => focusable.focus(), 50);
  };
  const closeDrawer = (d) => {
    const wasOpen = d.classList.contains('is-open');
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (!wasOpen) return;
    (d._inerted || []).forEach((el) => el.removeAttribute('inert'));
    d._inerted = null;
    const back = d._opener;
    d._opener = null;
    const fallback = () => $('header [data-open-drawer="' + d.id + '"]') || $('[data-open-drawer="' + d.id + '"]');
    const target = back && back.isConnected && !back.disabled && back.getClientRects().length > 0 ? back : fallback();
    if (target && target.focus) target.focus();
  };
  window.ASE.openDrawer = openDrawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.openDrawer, opener); return; }
    const closer = e.target.closest('[data-close-drawer]');
    if (closer) { closeDrawer(closer.closest('.drawer')); return; }

    const step = e.target.closest('[data-qty-dec], [data-qty-inc]');
    if (step) {
      e.preventDefault();
      const wrap = step.closest('.qty-step');
      const cur = Number(wrap.querySelector('input').dataset.was);
      Cart.setLine(wrap, 'qtyInc' in step.dataset ? cur + 1 : cur - 1);
      return;
    }

    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      if ('cartGift' in remove.dataset) { try { sessionStorage.setItem('ase_gift_off', '1'); } catch (err) { /* ignore */ } }
      Cart.change(Number(remove.dataset.cartRemove), 0); return;
    }

    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      addWithFeedback(quick, [{ id: Number(quick.dataset.quickAdd), quantity: 1 }]);
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest && e.target.closest('.qty-step__input');
    if (input) Cart.setLine(input.closest('.qty-step'), input.value === '' ? NaN : Number(input.value));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches && e.target.matches('.qty-step__input')) { e.preventDefault(); e.target.blur(); }
    if (e.key === 'Escape') $$('.drawer.is-open').forEach(closeDrawer);
    if (e.key === 'Tab') {
      const d = $('.drawer.is-open');
      if (!d) return;
      const f = drawerFocusables(d);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1], at = document.activeElement;
      if (!d.contains(at)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && at === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    }
  });

  async function addWithFeedback(btn, items, { openCart = true } = {}) {
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      await Cart.add(items);
      btn.classList.add('is-done');
      if (btn.dataset.doneLabel !== undefined) btn.innerHTML = btn.dataset.doneLabel || '✓';
      if (openCart) openDrawer('CartDrawer', btn);
    } catch (err) {
      alertInline(btn, err.message);
    } finally {
      btn.removeAttribute('aria-busy');
      setTimeout(() => { btn.disabled = false; btn.classList.remove('is-done'); btn.innerHTML = label; }, 1600);
    }
  }
  window.ASE.addWithFeedback = addWithFeedback;

  function alertInline(near, msg) {
    const n = document.createElement('div');
    n.className = 'notice notice--err';
    n.setAttribute('role', 'alert');
    n.textContent = msg;
    near.insertAdjacentElement('afterend', n);
    setTimeout(() => n.remove(), 5000);
  }

  /* Product form (PDP) */
  $$('form[data-product-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }];
      addWithFeedback(form.querySelector('[type=submit]'), items);
    });
  });

  /* Quantity steppers */
  document.addEventListener('click', (e) => {
    /* buttons only: the kit builder's tick boxes carry data-qty too (their quantity), and this used to add 1 to their variant id on every click */
    const b = e.target.closest('button[data-qty]');
    if (!b) return;
    const input = b.parentElement.querySelector('input');
    const next = Math.max(1, Number(input.value || 1) + Number(b.dataset.qty));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Tip families: the picked size's stock line, from its own reads (main-product data-variant-stock-meta: variant
     metafields ase.portal_stock and ase.stock_status). Same rules and 25 hour age check as snippets/stock-label.liquid
     'line', run at view time so a cached page never claims stock off a stale read. Keep the two in step. */
  const escHtml = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  /* The portal's estimated shipment date for an on order line, as snippets/stock-label.liquid words it: "around" the
     window start, "by" the window end when the start has passed, nothing when the whole window is past or absent. */
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function etaShown(p) {
    const ok = (d) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d);
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    let d = null; let word = 'around';
    if (ok(p.eta) && p.eta >= today) d = p.eta;
    else if (ok(p.eta) && ok(p.eta_to) && p.eta_to >= today) { d = p.eta_to; word = 'by'; }
    if (!d) return '';
    const [y, m, day] = d.split('-').map(Number);
    return `${word} ${day} ${MON[m - 1]}${y !== now.getFullYear() ? ` ${y}` : ''}`;
  }
  /* GO's own shelf at Deception Bay QLD (variant ase.go_stock, ops/sync_go_stock.mjs): the count when the read is under
     49 hours old, else 0. Same rule as snippets/stock-label.liquid and tip-pdp.js shipsNow. */
  const goQty = (g) => {
    if (!g || !Number.isInteger(g.qty) || g.qty <= 0) return 0;
    const t = Date.parse(g.checked_at);
    return Number.isFinite(t) && (Date.now() - t) / 3600e3 < 49 ? g.qty : 0;
  };
  const fmtChecked = (iso) => {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    const h = d.getHours() % 12 || 12;
    return `${d.getDate()} ${MON[d.getMonth()]}, ${h}:${String(d.getMinutes()).padStart(2, '0')}${d.getHours() < 12 ? 'am' : 'pm'}`;
  };
  function stockLine(meta, id) {
    const v = meta && meta.variants && meta.variants[id];
    if (!v) return null;
    const p = v.portal;
    const DUE = 'Available to order. ETA confirmed within 1 business day';
    let state = 'check';
    let text = meta.requestLabel || 'Request ETA';
    let checked = '';
    let gracoShips = false;
    if (v.own && meta.source !== 'graco_portal') {
      state = 'in';
      text = 'In stock, ready to ship';
    } else if (meta.source === 'graco_portal' && p) {
      checked = v.checked || '';
      let st = p.status;
      if (!st && v.stock_status === 'Ships now') st = 'in_stock';
      else if (!st && v.stock_status === 'On order') st = 'out';
      if (st === 'in_stock') state = 'in';
      else if (st === 'low') state = 'low';
      else if (st === 'out' || st === 'backorder') {
        state = 'out';
        text = 'On order, ETA on request';
        const when = etaShown(p);
        if (when) text = `On order. Expected from Graco Australia ${when}`;
      }
      if (state === 'in' || state === 'low') {
        gracoShips = true;
        text = Number.isInteger(p.qty) ? `Derrimut, VIC only: ${p.qty} in stock, ships in 1 to 2 business days` : 'Derrimut, VIC only: in stock, ships in 1 to 2 business days';
      }
      const ageH = Math.floor((Date.now() / 1000 - (Number(v.ts) || 0)) / 3600);
      if (ageH >= 25) { state = 'check'; text = DUE; checked = ''; gracoShips = false; }
    } else {
      text = DUE;
    }
    const go = meta.source === 'graco_portal' ? goQty(v.go) : 0;
    if (go > 0) {
      state = 'in';
      text = `In stock: ${go} at Deception Bay QLD`;
      if (gracoShips) text += Number.isInteger(p && p.qty) ? `, ${p.qty} at Derrimut, VIC` : ', more at Derrimut, VIC';
      checked = fmtChecked(v.go.checked_at);
    }
    return `<span class="stock stock--${state}"><span class="dot"></span><span>${escHtml(text)}${checked ? `<span class="muted" style="font-weight:400"> · ${escHtml(meta.checkLabel || '')} ${escHtml(checked)}</span>` : ''}</span></span>`;
  }
  /* Click and collect: say Deception Bay pickup is available now when GO holds the picked size */
  const pickupLine = (meta, id) => {
    const v = meta && meta.variants && meta.variants[id];
    const go = v && meta.source === 'graco_portal' ? goQty(v.go) : 0;
    if (go > 0) return ` Ready for collection within the hour (${go} in stock there now).`;
    return v && meta.source === 'graco_portal' ? ' Usually ready in 3 to 5 days.' : '';
  };
  const stockMeta = (root) => {
    const el = $('[data-variant-stock-meta]', root);
    if (!el) return null;
    try { return el._d || (el._d = JSON.parse(el.textContent)); } catch (err) { return null; }
  };
  const showStock = (root, id) => {
    const html = stockLine(stockMeta(root), id);
    if (html) $$('[data-variant-stock]', root).forEach((el) => { el.innerHTML = html; });
    if (html) $$('[data-go-pickup]', root).forEach((el) => { el.textContent = pickupLine(stockMeta(root), id); });
    return !!html;
  };
  /* On load: the page may be cached, so re-run the age check for the size it opened on */
  $$('[data-pdp]').forEach((root) => {
    const idIn = $('input[name="id"]', root);
    if (idIn && idIn.value) showStock(root, idIn.value);
  });

  /* Variant picker */
  $$('[data-variant-picker]').forEach((picker) => {
    const variants = JSON.parse(picker.querySelector('[type="application/json"]').textContent);
    const root = picker.closest('[data-pdp]');
    picker.addEventListener('change', () => {
      const chosen = $$('input:checked', picker).map((i) => i.value);
      const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
      if (!v) return;
      $$('input[name="id"]', root).forEach((i) => { i.value = v.id; });
      $$('[data-price]', root).forEach((el) => { el.textContent = formatMoney(v.price); });
      $$('[data-sku]', root).forEach((el) => { el.textContent = v.sku || ''; });
      $$('[data-atc]', root).forEach((btn) => {
        btn.disabled = !v.available;
        btn.querySelector('[data-atc-label]').textContent = v.available ? btn.dataset.labelAdd : ASE.strings.soldOut;
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState({}, '', url);
      /* Tip families: that size's stock and size specs (main-product data-variant-extras) */
      const ex = $('[data-variant-extras]', root);
      if (ex) {
        try {
          const all = ex._d || (ex._d = JSON.parse(ex.textContent));
          const x = all[v.id];
          if (x) {
            if (!showStock(root, v.id)) $$('[data-variant-stock]', root).forEach((el) => { el.innerHTML = x.stock; });
            $$('[data-variant-specs]', root).forEach((el) => { el.innerHTML = x.specs; el.style.setProperty('--k', String((x.specs.match(/class="spec /g) || []).length)); });
          }
        } catch (err) { /* keep what the page rendered */ }
      }
      if (v.featured_media) {
        const t = $(`[data-media-id="${v.featured_media}"]`, root);
        if (t) t.click();
      }
    });
  });

  /* Gallery */
  $$('[data-gallery]').forEach((g) => {
    const main = $('[data-gallery-main] img', g);
    $$('[data-media-id]', g).forEach((btn) => {
      btn.addEventListener('click', () => {
        $$('[data-media-id]', g).forEach((b) => b.setAttribute('aria-current', 'false'));
        btn.setAttribute('aria-current', 'true');
        main.src = btn.dataset.src;
        main.srcset = btn.dataset.srcset || '';
        main.alt = btn.querySelector('img').alt;
      });
    });
  });

  /* Sticky add to cart on mobile */
  const atc = $('[data-sticky-atc]');
  const anchor = $('[data-atc-anchor]');
  if (atc && anchor && 'IntersectionObserver' in window) {
    // Shown once the buy row has scrolled away, hidden again when the footer comes into view so footer links stay tappable.
    let pastBuy = false, atFooter = false;
    const sync = () => atc.classList.toggle('is-visible', pastBuy && !atFooter);
    new IntersectionObserver(([en]) => {
      pastBuy = !en.isIntersecting && en.boundingClientRect.top < 0; sync();
    }).observe(anchor);
    const foot = $('.site-footer');
    if (foot) new IntersectionObserver(([en]) => { atFooter = en.isIntersecting; sync(); }).observe(foot);
  }

  /* Reveal on scroll */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    $$('[data-reveal]').forEach((el) => io.observe(el));
  } else {
    $$('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  }

  /* Machine finder: filters a JSON list of machines by job, area and power */
  $$('[data-finder]').forEach((f) => {
    const machines = JSON.parse($('[type="application/json"]', f).textContent);
    const out = $('[data-finder-result]', f);
    const state = {};
    let showAll = false;
    f.addEventListener('click', (e) => {
      const more = e.target.closest('[data-finder-all]');
      if (more) { showAll = !showAll; render(); return; }
      const chip = e.target.closest('[data-finder-opt]');
      if (!chip) return;
      const key = chip.dataset.finderKey;
      $$(`[data-finder-key="${key}"]`, f).forEach((c) => c.setAttribute('aria-pressed', 'false'));
      chip.setAttribute('aria-pressed', 'true');
      /* "One painter" is the plain list: it takes the filter off rather than adding one */
      if (key === 'painters' && chip.dataset.finderOpt === 'one') delete state[key]; else state[key] = chip.dataset.finderOpt;
      showAll = false;
      render();
    });
    /* The optional painters question does not count as one the shopper still has to answer */
    const qCount = new Set($$('[data-finder-key]:not([data-finder-optional])', f).map((c) => c.dataset.finderKey)).size;
    const esc = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    function render() {
      const keys = Object.keys(state);
      if (!keys.length) { out.innerHTML = ''; return; }
      const two = state.painters === 'two';
      /* Every machine that matches every answer so far, so entry sprayers are never cut off. Lowest price first; with two
         painters, only machines that list a two handpiece max tip, biggest tip first, then lowest price. */
      const all = machines
        .filter((m) => keys.every((k) => (k === 'painters' ? !!m.two : (m.tags || []).includes(`${k}:${state[k]}`))))
        .sort((x, y) => (two ? (y.twon || 0) - (x.twon || 0) : 0) || (x.cents || 0) - (y.cents || 0));
      const n = all.length;
      const answered = keys.filter((k) => k !== 'painters').length;
      const done = answered >= qCount;
      const order = two ? 'biggest two gun tip first, then lowest price' : 'lowest price first';
      const shown = showAll ? all : all.slice(0, 3);
      const head = !n ? ''
        : done
          ? `${n} match${n > 1 ? 'es' : ''}, ${order}${n > 3 && !showAll ? '. Showing 3' : ''}`
          : `${n} sprayer${n > 1 ? 's' : ''} so far, ${order}. Answer the other question${qCount - answered > 1 ? 's' : ''} to narrow it`;
      out.innerHTML = n
        ? `<p class="eyebrow">${head}</p>` + shown.map((m) => `
          <a class="card" href="${m.url}" style="flex-direction:row;align-items:center">
            <div class="card__media" style="width:96px;flex:0 0 96px;padding:8px">${m.image ? `<img src="${m.image}" alt="" loading="lazy" width="96" height="96">` : ''}</div>
            <div class="card__body"><span class="card__brand">${m.vendor}</span><span class="card__title">${m.title}</span><span class="price">${m.price}</span>${two ? `<span class="muted" style="font-size:13px">Two guns: ${esc(m.two)}</span>` : ''}</div>
          </a>`).join('') + (n > 3 ? `<p style="margin:12px 0 0"><button type="button" class="btn btn--sm btn--line" data-finder-all aria-expanded="${showAll}">${showAll ? 'Show fewer' : `Show all ${n}`}</button></p>` : '')
        : `<p class="muted">No exact match. Take one answer off, or email us the job and we'll suggest a sprayer.</p>`;
    }
  });

  /* Price and availability request (Wagner and Titan) */
  $$('[data-pa-form]').forEach((form) => {
    form.addEventListener('submit', () => {
      const btn = form.querySelector('[type=submit]');
      if (btn) { btn.disabled = true; btn.textContent = ASE.strings.adding; }
    });
  });

  Cart.refreshCount().catch(() => {});

  /* Back to a page from checkout or another page: the restored drawer is out of date, so re-read it now and mark it
     to re-read once more when it opens. Covers bfcache (persisted) and the HTTP cache restore (back_forward). */
  const markStale = () => { const d = document.getElementById('CartDrawer'); if (d) d._stale = true; };
  const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (nav && nav.type === 'back_forward') { Cart.refresh().catch(() => {}); markStale(); }
  window.addEventListener('pageshow', (e) => { if (e.persisted) { Cart.refresh().catch(() => {}); markStale(); } });
})();
